import React from 'react';
import { motion } from 'framer-motion';
import appleIcon from '../assets/apple-icon.svg';
import appPhoto from '../assets/appPhoto.png';
import rtIcon from '../assets/RT_main_icon M.png';

const FloatingChip = ({ 
  emoji, 
  label, 
  className, 
  delay = 0 
}: { 
  emoji: string, 
  label: string, 
  className?: string,
  delay?: number 
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.6, ease: "easeOut" }}
      className={`absolute flex items-center gap-2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm px-3 py-2 sm:px-4 sm:py-3 rounded-xl sm:rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 z-20 transition-colors text-sm sm:text-base ${className}`}
    >
      <span className="text-base sm:text-lg">{emoji}</span>
      <span className="font-medium text-gray-800 dark:text-gray-200 whitespace-nowrap">{label}</span>
  </motion.div>
);

export const Hero = () => {
  return (
    <div className="min-h-screen flex flex-col lg:flex-row items-center justify-center max-w-[1800px] mx-auto px-6 py-12 lg:py-0 gap-12 lg:gap-24">
      
      {/* Left Side - Phone Mockup */}
      <div className="relative w-full lg:w-auto lg:flex-shrink-0 flex justify-center items-center bg-transparent px-4 sm:px-0">
        {/* App Photo */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="relative z-10 w-full max-w-[280px] sm:max-w-[350px] md:max-w-[450px] lg:max-w-[550px] xl:max-w-[650px] 2xl:max-w-[750px] bg-transparent"
        >
          <img 
            src={appPhoto} 
            alt="ReelTravel App Interface" 
            className="w-full h-auto object-contain drop-shadow-2xl bg-transparent"
            loading="eager"
            decoding="async"
            style={{ 
              imageRendering: 'auto',
              maxWidth: '100%',
              height: 'auto'
            }}
          />
        </motion.div>

        {/* Floating Chips */}
        <FloatingChip 
          emoji="📍" 
          label="Hidden Gems" 
          className="top-[15%] sm:top-[20%] left-2 sm:left-4 lg:-left-12" 
          delay={0.2}
        />
        <FloatingChip 
          emoji="✈️" 
          label="Plan Trips" 
          className="bottom-[25%] sm:bottom-[30%] right-2 sm:right-4 lg:-right-8" 
          delay={0.4}
        />
        <FloatingChip 
          emoji="🗺️" 
          label="Itineraries" 
          className="bottom-[10%] sm:bottom-[15%] left-2 sm:left-4 lg:-left-6" 
          delay={0.6}
        />
      </div>

      {/* Right Side - Content */}
      <div className="flex-1 flex flex-col items-center lg:items-start text-center lg:text-left space-y-8 max-w-2xl lg:max-w-none lg:w-full">
        
        {/* Logo */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center lg:items-start"
        >
          <img 
            src={rtIcon} 
            alt="ReelTravel Logo" 
            className="w-16 h-16 mb-4 object-contain"
          />
          <h1 className="text-3xl lg:text-4xl text-black dark:text-white transition-colors" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Segoe UI", Roboto, sans-serif', fontWeight: 600 }}>
            ReelTravel
          </h1>
        </motion.div>

        {/* Headline */}
        <div className="space-y-4">
          <h2 className="font-serif text-3xl lg:text-4xl leading-tight text-gray-900 dark:text-gray-100 transition-colors">
            The First AI Travel Planner<br />Powered by Reels
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed max-w-md mx-auto lg:mx-0 transition-colors">
            Import your reels, Lets us plan, research and build your trip
          </p>
        </div>

        {/* CTA Button */}
        <motion.a
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          href="#"
          className="bg-black dark:bg-white text-white dark:text-black px-8 py-3 rounded-xl flex items-center gap-3 shadow-lg hover:shadow-xl transition-all"
        >
          <img src={appleIcon} alt="Apple" className="w-6 h-6 brightness-0 invert dark:brightness-0 dark:invert-0" />
          <div className="text-left">
            <div className="text-[10px] uppercase tracking-wider opacity-80">Download on the</div>
            <div className="text-lg font-bold leading-none">App Store</div>
          </div>
        </motion.a>

      </div>
    </div>
  );
};
